<template>
  <b-modal :id="modalId" :title="isEditing ? 'Edit Lesson' : 'Add New Lesson'" size="lg" centered @hidden="resetModal">
    <b-form @submit.prevent="handleSubmit">
      <!-- Title -->
      <b-form-group label="Title" label-for="lesson-title" label-class="form-label">
        <b-form-input id="lesson-title" v-model="formData.title" placeholder="Enter lesson title" required />
      </b-form-group>

      <!-- Description -->
      <b-form-group label="Description" label-for="lesson-description" label-class="form-label">
        <div class="editor-container">
          <editor
            :key="'editor-' + editorKey"
            :api-key="tinemceApiKey"
            :init="{
              height: 400,
              menubar: false,
              plugins: [
                'advlist autolink lists link',
                'searchreplace visualblocks',
                'insertdatetime media table paste',
                'help wordcount code preview',
              ],
              toolbar:
                'undo redo | formatselect | bold italic backcolor | ' +
                'alignleft aligncenter alignjustify | ' +
                'bullist numlist outdent indent | removeformat | help',
              statusbar: false,
              branding: false,
            }"
            v-model="formData.description"
            placeholder="Enter lesson description"
          />
        </div>
      </b-form-group>

      <!-- Estimated Time -->
      <b-form-group label="Estimated Content Time" label-for="estimated_time_minutes" label-class="form-label">
        <time-picker
          v-model="formData.estimated_time_minutes"
          input-id="estimated_time_minutes"
          :show-total-minutes="true"
        />
      </b-form-group>

      <!-- Video URL -->
      <div class="row mb-4">
        <span class="form-label col-md-6">Lesson Video</span>
        <div class="col-md-6" style="display: flex; justify-content: end;">
          <b-btn
            v-if="!formData.content_video"
            variant="primary"
            :class="{ 'mb-2': formData.content_video }"
            style="width: 150px"
            @click.prevent="$refs.uploader.click()"
            class="btn-normal"
            :disabled="isUploadingVideoUrl"
          >
            <i v-if="isUploadingVideoUrl" class="fas fa-circle-notch fa-spin"></i>
            <span v-else>Upload Video</span>
          </b-btn>
          <b-btn
            v-else
            variant="danger"
            :class="{ 'mb-2': formData.content_video }"
            style="width: 150px"
            @click.prevent="removeVideo"
            class="btn-normal"
            :disabled="isUploadingVideoUrl"
          >
            <span>Remove Video</span>
          </b-btn>
          <input type="file" ref="uploader" class="d-none" @change="handleFileChange" accept="video/*" />
        </div>
        <div v-if="formData.content_video" class="d-flex align-items-center col">
          <video
            style="width: 100%; margin: auto;"
            :style="{ height: isMobileScreen ? '230px' : '450px' }"
            id="video-preview"
            controls
            :src="formData.content_video"
          />
        </div>
      </div>

      <!-- PDF URL -->
      <div class="row mb-4">
        <span class="form-label col-md-6">Lesson PDF</span>
        <div class="col-md-6" style="display: flex; justify-content: end;">
          <b-btn
            v-if="!formData.content_pdf_url"
            variant="primary"
            :class="{ 'mb-2': formData.content_pdf_url }"
            style="width: 150px"
            @click.prevent="$refs.pdfUploader.click()"
            class="btn-normal"
            :disabled="isUploadingPdfUrl"
          >
            <i v-if="isUploadingPdfUrl" class="fas fa-circle-notch fa-spin"></i>
            <span v-else>Upload PDF</span>
          </b-btn>
          <b-btn
            v-else
            variant="danger"
            :class="{ 'mb-2': formData.content_pdf_url }"
            style="width: 150px"
            @click.prevent="removePdf"
            class="btn-normal"
            :disabled="isUploadingPdfUrl"
          >
            <span>Remove PDF</span>
          </b-btn>
          <input type="file" ref="pdfUploader" class="d-none" @change="handlePdfFileChange" accept="application/pdf" />
        </div>
        <div v-if="formData.content_pdf_url" class="d-flex align-items-center col">
          <div class="pdf-preview-container" style="width: 100%;">
            <iframe
              :src="formData.content_pdf_url"
              style="width: 100%; border: 1px solid #dae1e7; border-radius: 0.25rem;"
              :style="{ height: isMobileScreen ? '230px' : '450px' }"
            ></iframe>
            <a :href="formData.content_pdf_url" target="_blank" class="btn btn-sm btn-outline-primary mt-2">
              <i class="fas fa-external-link-alt mr-1"></i>Open PDF in new tab
            </a>
          </div>
        </div>
      </div>

      <!-- Order -->
      <!-- <b-form-group label="Order" label-for="lesson-order" label-class="form-label">
        <b-form-input id="lesson-order" v-model.number="formData.order" type="number" min="1" required />
      </b-form-group> -->
    </b-form>

    <b-alert v-if="showComponentChangeWarning" variant="warning" show class="mb-3">
      Updating the assignment or assessment for this lesson may result in grading weightage issues if there are already submissions against it in this course.
    </b-alert>

    <b-form-group v-if="assignments.length > 0" label-for="assignment" label-class="form-label">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <label class="form-label mb-0"
          >Assignment
          <i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top="'This assignment will be taken at the end of the Lesson.'"
          >
            info
          </i></label
        >
        <b-badge v-if="formData.assignment_weightage !== undefined && formData.assignment_weightage > 0" variant="warning" class="ml-2">
          Weightage: {{ formData.assignment_weightage }}%
        </b-badge>
      </div>
      <v-select
        required
        id="assignment"
        class="form-control v-select-custom"
        label="title"
        v-model="formData.assignment"
        :reduce="item => item.id"
        placeholder="Select Assignment"
        :options="assignments"
        :loading="areAssignmentsLoading"
      >
        <template #option="option">
          <div style="display: flex; align-items: center; width: 100%; gap: 8px;">
            <span style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ option.title }}</span>
            <b-badge v-if="option.is_graded && option.weightage !== undefined" variant="warning" style="white-space: nowrap;">
              {{ option.weightage }}%
            </b-badge>
            <span v-if="option.estimated_time_minutes" class="text-muted" style="font-size: 0.875rem; white-space: nowrap;">
              <i class="far fa-clock mr-1"></i>{{ option.estimated_time_minutes }} min
            </span>
          </div>
        </template>
        <template #selected-option="option">
          <span class="selected-title">{{ option.title }}</span>
          <b-badge v-if="option.is_graded && option.weightage !== undefined" variant="warning" class="selected-badge">
            {{ option.weightage }}%
          </b-badge>
          <span v-if="option.estimated_time_minutes" class="selected-time text-muted">
            <i class="far fa-clock mr-1"></i>{{ option.estimated_time_minutes }} min
          </span>
        </template>
      </v-select>
    </b-form-group>
    <b-form-group label-for="quiz" label-class="form-label">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <label class="form-label mb-0"
          >Assessment
          <i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top="'This assessment will be taken at the end of the Lesson.'"
          >
            info
          </i></label
        >
        <b-badge v-if="formData.quiz_weightage !== undefined && formData.quiz_weightage > 0" variant="info" class="ml-2">
          Weightage: {{ formData.quiz_weightage }}%
        </b-badge>
      </div>
      <v-select
        required
        id="quiz"
        class="form-control v-select-custom"
        label="title"
        v-model="formData.quiz"
        :reduce="item => item.id"
        placeholder="Select Assessment"
        :options="quizzes"
        :loading="areQuizzesLoading"
      >
        <template #option="option">
          <div style="display: flex; align-items: center; width: 100%; gap: 8px;">
            <span style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ option.title }}</span>
            <b-badge v-if="option.weightage !== undefined" variant="info" style="white-space: nowrap;">
              {{ option.weightage }}%
            </b-badge>
            <span v-if="option.duration_minutes" class="text-muted" style="font-size: 0.875rem; white-space: nowrap;">
              <i class="far fa-clock mr-1"></i>{{ option.duration_minutes }} min
            </span>
          </div>
        </template>
        <template #selected-option="option">
          <span class="selected-title">{{ option.title }}</span>
          <b-badge v-if="option.weightage !== undefined" variant="info" class="selected-badge">
            {{ option.weightage }}%
          </b-badge>
          <span v-if="option.duration_minutes" class="selected-time text-muted">
            <i class="far fa-clock mr-1"></i>{{ option.duration_minutes }} min
          </span>
        </template>
      </v-select>
    </b-form-group>

    <template #modal-footer>
      <div class="w-100">
        <b-button variant="outline-secondary" class="float-right" @click="$bvModal.hide(modalId)">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          class="float-right mr-2"
          @click="handleSubmit"
          :disabled="isSubmitting || isUploadingVideoUrl || isUploadingPdfUrl"
        >
          <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
          {{ isEditing ? 'Save' : 'Add' }} Lesson
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import Editor from '@tinymce/tinymce-vue';
import TimePicker from '@/components/TimePicker.vue';
import { mapActions } from 'vuex';

export default {
  name: 'LessonModal',
  components: {
    Editor,
    TimePicker,
  },
  props: {
    modalId: {
      type: String,
      required: true,
      default: 'lesson-modal',
    },
    quizzes: {
      type: Array,
      default: () => [],
    },
    areQuizzesLoading: {
      type: Boolean,
      default: false,
    },

    lesson: {
      type: Object,
      default: () => ({
        title: '',
        description: '',
        content_video: '',
        content_pdf_url: '',
        order: 1,
        quiz: null,
        assignment: null,
        component_update_warning: false,
        estimated_time_minutes: null,
      }),
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    programId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      tinemceApiKey: process.env.VUE_APP_TINEMCE_API_KEY,
      editorKey: 0,
      originalAssignmentId: null,
      originalQuizId: null,
      formData: {
        title: '',
        description: '',
        content_video: '',
        content_pdf_url: '',
        order: 1,
        quiz: null,
        assignment: null,
        estimated_time_minutes: null,
      },
      isSubmitting: false,
      isUploadingVideoUrl: false,
      isUploadingPdfUrl: false,
      windowWidth: window.innerWidth,
      isMounted: false,
      areAssignmentsLoading: false,
      assignments: [],
      filteredAssignments: [],
    };
  },
  watch: {
    lesson: {
      handler(newVal) {
        if (newVal) {
          // Create a new object to trigger reactivity
          this.formData = { ...newVal };
          // Force re-render editor when lesson changes
          if (this.isMounted) {
            this.$nextTick(() => {
              this.editorKey += 1;
            });
          }
        }
      },
      immediate: true,
      deep: true,
    },
    programId: {
      handler() {
        this.fetchAssignments();
      },
    },
    'formData.assignment': {
      handler(newVal) {
        if (newVal) {
          const selectedAssignment = this.assignments.find(a => a.id === newVal);
          if (selectedAssignment && selectedAssignment.is_graded && selectedAssignment.weightage) {
            this.$set(this.formData, 'assignment_weightage', parseFloat(selectedAssignment.weightage));
          } else {
            this.$set(this.formData, 'assignment_weightage', 0);
          }
        } else {
          this.$set(this.formData, 'assignment_weightage', 0);
        }
      },
    },
    'formData.quiz': {
      handler(newVal) {
        if (newVal) {
          const selectedQuiz = this.quizzes.find(q => q.id === newVal);
          if (selectedQuiz && selectedQuiz.weightage !== undefined) {
            this.$set(this.formData, 'quiz_weightage', parseFloat(selectedQuiz.weightage));
          } else {
            this.$set(this.formData, 'quiz_weightage', 0);
          }
        } else {
          this.$set(this.formData, 'quiz_weightage', 0);
        }
      },
    },
  },
  computed: {
    isMobileScreen() {
      return this.windowWidth <= 420;
    },
    showComponentChangeWarning() {
      if (!this.isEditing) return false;

      const originalAssignmentId =
        this.originalAssignmentId && typeof this.originalAssignmentId === 'object'
          ? this.originalAssignmentId.id
          : this.originalAssignmentId;
      const originalQuizId =
        this.originalQuizId && typeof this.originalQuizId === 'object' ? this.originalQuizId.id : this.originalQuizId;

      const currentAssignmentId =
        this.formData.assignment && typeof this.formData.assignment === 'object'
          ? this.formData.assignment.id
          : this.formData.assignment;
      const currentQuizId =
        this.formData.quiz && typeof this.formData.quiz === 'object' ? this.formData.quiz.id : this.formData.quiz;

      return originalAssignmentId !== currentAssignmentId || originalQuizId !== currentQuizId;
    },
    filteredQuizzes() {
      if (!this.programId) return this.quizzes;
      return this.quizzes.filter(quiz => {
        return quiz.program && quiz.program.id === this.programId;
      });
    },
  },
  mounted() {
    this.fetchAssignments();
    this.isMounted = true;
    // Ensure TinyMCE is properly initialized after the modal is shown
    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === this.modalId) {
        // Reset form data when modal is shown
        if (this.lesson) {
          this.formData = { ...this.lesson };
          this.originalAssignmentId =
            this.lesson.assignment && typeof this.lesson.assignment === 'object'
              ? this.lesson.assignment.id
              : this.lesson.assignment;
          this.originalQuizId =
            this.lesson.quiz && typeof this.lesson.quiz === 'object' ? this.lesson.quiz.id : this.lesson.quiz;
        }
        this.$nextTick(() => {
          this.editorKey += 1;
        });
      }
    });
  },

  beforeDestroy() {
    this.$root.$off('bv::modal::show');
    this.isMounted = false;
  },

  methods: {
    ...mapActions('lessons', ['getLessonImageUploadPresignedUrl']),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    ...mapActions('assignments', ['getAssignmentsList']),

    resetModal() {
      this.formData = {
        title: '',
        description: '',
        content_video: '',
        content_pdf_url: '',
        order: 1,
        quiz: null,
        assignment: null,
        estimated_time_minutes: null,
      };
      this.originalAssignmentId = null;
      this.originalQuizId = null;
      // Reset the editor
      this.$nextTick(() => {
        this.editorKey += 1;
      });
      this.$emit('reset');
    },
    async handleSubmit() {
      this.isSubmitting = true;
      try {
        const lessonData = { 
          ...this.formData,
          // Include computed weightages for display purposes
          computed_weightage: (this.formData.quiz_weightage || 0) + (this.formData.assignment_weightage || 0)
        };
        this.$emit('submit', lessonData);
        this.$bvModal.hide(this.modalId);
      } catch (error) {
        console.error('Error saving lesson:', error);
        this.makeToast({ variant: 'danger', msg: 'Failed to save lesson. Please try again.' });
      } finally {
        this.isSubmitting = false;
      }
    },
    removeVideo() {
      this.formData.content_video = '';
    },
    removePdf() {
      this.formData.content_pdf_url = '';
    },
    async fetchAssignments() {
      this.areAssignmentsLoading = true;
      try {
        const params = this.programId ? { program: this.programId } : {};
        const resp = await this.getAssignmentsList(params);
        this.assignments = resp.data;
        this.filteredAssignments = this.assignments;
      } catch (error) {
        console.error('Error fetching assignments:', error);
      }
      this.areAssignmentsLoading = false;
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadVideo(file);
      }
    },
    async uploadVideo(file) {
      this.isUploadingVideoUrl = true;

      if (file.size > 250000000) {
        this.makeToast({ variant: 'danger', msg: 'File size should not exceed 250MB' });
        this.isUploadingVideoUrl = false;
        return;
      }
      if (!['video/mp4', 'video/webm'].includes(file.type)) {
        this.makeToast({ variant: 'danger', msg: 'File type should be mp4 or webm' });

        this.isUploadingVideoUrl = false;
        return;
      }

      try {
        const urlResp = await this.getLessonImageUploadPresignedUrl({
          file_name: file.name,
          content_type: file.type,
        });
        await this.uploadToPresignedUrl({
          url: urlResp.data.upload_url,
          file: file,
        });
        this.formData.content_video = urlResp.data.upload_url.split('?')[0];
      } catch (error) {
        this.isUploadingVideoUrl = false;
        return;
      }
      this.isUploadingVideoUrl = false;
    },
    handlePdfFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadPdf(file);
      }
    },
    async uploadPdf(file) {
      this.isUploadingPdfUrl = true;

      if (file.size > 25000000) {
        this.makeToast({ variant: 'danger', msg: 'File size should not exceed 25MB' });
        this.isUploadingPdfUrl = false;
        return;
      }
      if (file.type !== 'application/pdf') {
        this.makeToast({ variant: 'danger', msg: 'File type should be PDF' });
        this.isUploadingPdfUrl = false;
        return;
      }

      try {
        const urlResp = await this.getLessonImageUploadPresignedUrl({
          file_name: file.name,
          content_type: file.type,
        });
        await this.uploadToPresignedUrl({
          url: urlResp.data.upload_url,
          file: file,
        });
        this.formData.content_pdf_url = urlResp.data.upload_url.split('?')[0];
      } catch (error) {
        this.isUploadingPdfUrl = false;
        return;
      }
      this.isUploadingPdfUrl = false;
    },
  },
};
</script>

<style scoped>
.tox-tinymce {
  border-radius: 0.25rem;
  border: 1px solid #dae1e7;
}
</style>

<style>
/* Position time next to the clear icon in v-select */
.v-select-custom .vs__selected-options {
  position: relative;
  flex-wrap: nowrap !important;
}

.v-select-custom .vs__selected {
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0;
  padding: 0;
}

.v-select-custom .selected-title {
  flex: 0 1 auto;
  white-space: nowrap;
  overflow: visible;
  margin-right: auto;
}

.v-select-custom .selected-badge {
  position: absolute;
  right: 120px;
  font-size: 0.75rem;
  white-space: nowrap;
  pointer-events: none;
}

.v-select-custom .selected-time {
  position: absolute;
  right: 35px;
  font-size: 0.875rem;
  white-space: nowrap;
  pointer-events: none;
}

.v-select-custom .vs__actions {
  position: relative;
  z-index: 1;
}
</style>
